import type {
  OrderDetail,
  OrderListFilters,
  OrderListItem,
  OrderListResponse,
  OrderReprocessItemResult,
} from "../types/orders";
import {
  canReprocessOrder,
  normalizeOrderListItemForDisplay,
  orderListItemToDetail,
  parseOrderTotalFilter,
} from "../types/orders";

/**
 * Mock aligned with reduced list-orders payload (§3.2).
 * Used when REACT_APP_USE_ORDERS_MOCK=true.
 */

const row = (
  partial: Omit<OrderListItem, "retryCount" | "attemptCount" | "orderSIESAId"> & {
    retryCount?: number | null;
    attemptCount?: number | null;
    orderSIESAId?: string | null;
  }
): OrderListItem => ({
  ...partial,
  orderSIESAId: partial.orderSIESAId ?? null,
  retryCount: partial.retryCount ?? null,
  attemptCount: partial.attemptCount ?? null,
});

const MOCK_ORDERS: OrderListItem[] = [
  row({
    orderVTEXId: "1653411108109-01",
    orderSIESAId: "413715",
    documentId: "1005583130",
    names: "María José González Ruiz",
    creationDate: "2026-08-11T08:04:37.000-05:00",
    trackingNumber: "2225474124",
    status: "created-order",
    total: 692000,
    processedAt: "2026-08-11T21:22:51.058554",
    errorDetail: null,
    retryCount: 4,
  }),
  row({
    orderVTEXId: "1649821076932-01",
    orderSIESAId: "413200",
    documentId: "1234567890",
    names: "Ana Gómez",
    creationDate: "2026-08-05T17:55:12.000+00:00",
    trackingNumber: null,
    status: "pending-create-order",
    total: 1013500,
    processedAt: "2026-08-05T18:01:44",
    errorDetail:
      "La orden ya fue creada en SIESA. Este error aparece al reprocesar porque el número de O.C. ya existe (no indica un fallo original de creación).",
    retryCount: 2,
  }),
  row({
    orderVTEXId: "VTEX-10002",
    documentId: "1098765432",
    names: "Carlos Gómez",
    creationDate: "2026-07-21T09:00:00",
    trackingNumber: null,
    status: "synchronized",
    total: 259900,
    processedAt: null,
    errorDetail: null,
    retryCount: 0,
  }),
  row({
    orderVTEXId: "VTEX-10003",
    documentId: "1020304050",
    names: "Laura Díaz",
    creationDate: "2026-07-22T11:30:00",
    trackingNumber: null,
    status: "pending-create-client",
    total: 189900,
    processedAt: "2026-07-22T11:35:00",
    errorDetail: "Falló resolución cliente/ciudad",
    retryCount: 1,
  }),
  row({
    orderVTEXId: "VTEX-10005",
    documentId: "1987654321",
    names: "Pedro Ruiz",
    creationDate: "2026-07-23T14:00:00",
    trackingNumber: "TRK-10005",
    status: "pending-generate-guide",
    total: 320000,
    processedAt: "2026-07-23T14:10:00",
    errorDetail: "Falló guía por homologación",
    retryCount: 3,
  }),
  row({
    orderVTEXId: "VTEX-10006",
    orderSIESAId: "412900",
    documentId: "111222333",
    names: "Sofía Rojas",
    creationDate: "2026-07-24T08:00:00",
    trackingNumber: "TRK-10006",
    status: "invoiced",
    total: 450000,
    processedAt: "2026-07-24T09:00:00",
    errorDetail: null,
    retryCount: 0,
  }),
  row({
    orderVTEXId: "VTEX-10011",
    documentId: "555666777",
    names: "Diego Castro",
    creationDate: "2026-07-25T10:00:00",
    trackingNumber: "TRK-10011",
    status: "created-client",
    total: 210000,
    processedAt: "2026-07-25T10:15:00",
    errorDetail: null,
    retryCount: 1,
  }),
  row({
    orderVTEXId: "VTEX-10012",
    documentId: "888999000",
    names: "Elena Vargas",
    creationDate: "2026-07-26T12:00:00",
    trackingNumber: null,
    status: "canceled",
    total: 99000,
    processedAt: null,
    errorDetail: null,
    retryCount: 0,
  }),
  row({
    orderVTEXId: "VTEX-10014",
    orderSIESAId: "413050",
    documentId: "444555666",
    names: "Andrés León",
    creationDate: "2026-07-27T16:00:00",
    trackingNumber: "TRK-10014",
    status: "generated-guide",
    total: 375000,
    processedAt: "2026-07-27T16:20:00",
    errorDetail: null,
    retryCount: 0,
  }),
  row({
    orderVTEXId: "1653501108113-01",
    documentId: "1001122334",
    names: "Cliente Demo",
    creationDate: "2026-08-11T09:00:00.000-05:00",
    trackingNumber: null,
    status: "canceled",
    total: 150000,
    processedAt: null,
    errorDetail: null,
    retryCount: 0,
  }),
];

let mutableOrders: OrderListItem[] = MOCK_ORDERS.map((o) => ({ ...o }));

/**
 * Mirrors GET /order/list-orders §3.2 (exact match + date range + 2-month window).
 * ASSUMPTION: mock uses `creationDate` as stand-in for `sale.create_at`.
 */
const matchesFilters = (
  order: OrderListItem,
  filters: OrderListFilters
): boolean => {
  const term = filters.search.trim();
  if (term) {
    if (filters.filterType === "ORDER_VTEX_ID") {
      if (order.orderVTEXId !== term) return false;
    } else if (filters.filterType === "ORDER_SIESA_ID") {
      if ((order.orderSIESAId ?? "") !== term) return false;
    } else if (filters.filterType === "TOTAL") {
      const total = parseOrderTotalFilter(term);
      if (total === null || Math.trunc(order.total) !== total) return false;
    }
  }

  if (filters.status !== "ALL" && order.status !== filters.status) {
    return false;
  }

  const createdMs = order.creationDate
    ? new Date(order.creationDate).getTime()
    : Number.NaN;

  // Backend always scopes to last 2 months even without fromDate/toDate.
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const windowStart = new Date(today);
  windowStart.setMonth(windowStart.getMonth() - 2);
  windowStart.setHours(0, 0, 0, 0);

  if (!Number.isNaN(createdMs)) {
    if (createdMs < windowStart.getTime() || createdMs > today.getTime()) {
      return false;
    }
  }

  if (filters.dateFrom || filters.dateTo) {
    if (Number.isNaN(createdMs)) return false;
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom).setHours(0, 0, 0, 0);
      if (createdMs < from) return false;
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo).setHours(23, 59, 59, 999);
      if (createdMs > to) return false;
    }
  }

  return true;
};

export const fetchOrdersMock = (
  page: number,
  size: number,
  filters: OrderListFilters
): OrderListResponse => {
  const filtered = mutableOrders.filter((order) =>
    matchesFilters(order, filters)
  );
  const start = (page - 1) * size;
  const content = filtered.slice(start, start + size);
  const totalPages = Math.ceil(filtered.length / size) || 0;

  return {
    content: content.map(normalizeOrderListItemForDisplay),
    totalElements: filtered.length,
    totalPages,
    currentPage: page,
    pageSize: size,
  };
};

export const fetchOrderDetailMock = (
  orderVTEXId: string
): OrderDetail | null => {
  const order = mutableOrders.find((o) => o.orderVTEXId === orderVTEXId);
  if (!order) return null;
  return orderListItemToDetail(normalizeOrderListItemForDisplay(order));
};

/** Single-order mock used by batch helper */
export const reprocessOrderMock = (
  orderId: string
): OrderReprocessItemResult => {
  const idx = mutableOrders.findIndex((o) => o.orderVTEXId === orderId);
  if (idx < 0) {
    return {
      orderId,
      success: false,
      skipped: false,
      message: "Order not found",
      statusBefore: null,
      statusAfter: null,
      processResult: null,
      errorDetail: "Order not found",
    };
  }

  const order = mutableOrders[idx];
  const statusBefore = order.status;

  if (!canReprocessOrder(order)) {
    return {
      orderId,
      success: false,
      skipped: true,
      message: `Order status is not reprocessable: ${statusBefore}`,
      statusBefore,
      statusAfter: statusBefore,
      processResult: "SUCCESS",
      errorDetail: null,
    };
  }

  const digit = Number(orderId.replace(/\D/g, "").slice(-1)) || 0;
  const succeed = digit % 3 !== 0;

  if (succeed) {
    mutableOrders[idx] = {
      ...order,
      status: "created-order",
      errorDetail: null,
      processedAt: new Date().toISOString(),
    };
    return {
      orderId,
      success: true,
      skipped: false,
      message: "Reprocess completed successfully",
      statusBefore,
      statusAfter: "created-order",
      processResult: "SUCCESS",
      errorDetail: null,
    };
  }

  mutableOrders[idx] = {
    ...order,
    errorDetail: "SIESA devolvió una novedad durante el reproceso simulado.",
    processedAt: new Date().toISOString(),
  };

  return {
    orderId,
    success: false,
    skipped: false,
    message: "SIESA connection timeout - Siesa client not created",
    statusBefore,
    statusAfter: statusBefore,
    processResult: "FAILED",
    errorDetail: "SIESA connection timeout",
  };
};

/** Batch mock: one result per id (mirrors §3.3 body). */
export const reprocessOrdersMock = (
  orderIds: string[]
): OrderReprocessItemResult[] => orderIds.map((id) => reprocessOrderMock(id));

export const resetOrdersMock = (): void => {
  mutableOrders = MOCK_ORDERS.map((o) => ({ ...o }));
};
